import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageContent, PageHeader, PageSection } from 'common/styles/page';
import {  HeaderCell,  Table,  TableBody,  TableCell,  TableHeader,  TableRow,} from 'common/components/Table';
import { useDispatch, useSelector } from 'react-redux';

import LandingPage from 'common/layouts/AuthLayout/LandingPage';
import LoadingSpinner from 'common/components/LoadingSpinner';
import DoughnutGauge from 'areas/dashboard/components/DoughnutGauge';

import { useApiPortalV1AssessmentsPlansOrganizationListQuery } from 'areas/assessment-schedule/api';
import { getCurrentOrganizationId, getMemberId } from 'common/selectors/auth';
import { RecommendationType } from 'common/constants/RecommendationType';
import { useApiPortalV1AssessmentsTemplatesListQuery } from 'store/generated/portal/assessments';

import { RecommendationTemplateRead } from 'store/generated/portal/assessments';

import { useToasts } from 'common/toast';

import { actionPlanApi } from 'areas/recommendations/api/actionPlanApi';

export const FoundationProjects = () => {
  const memberId = useSelector(getMemberId);
  const organizationId = useSelector(getCurrentOrganizationId);

  const [isSaving, setIsSaving] = useState(false);
  const dispatch = useDispatch();

  const { addToast } = useToasts();

  const { data: foundation_data } = useApiPortalV1AssessmentsPlansOrganizationListQuery({
    typeOf: RecommendationType.FOUNDATIONS,
  });
  const [createAssessmentPlan] =
    actionPlanApi.useApiPortalV1AssessmentsPlansOrganizationCreateMutation();

  const { data: rec_template_data } = useApiPortalV1AssessmentsTemplatesListQuery({
    typeOf: RecommendationType.FOUNDATIONS,
  });

  const navigate = useNavigate();

  const handleRowClick = (action_plan_id: number) => {
    navigate(`edit/${action_plan_id}`);
  };

  // Genereate Project Plan
  const onSave = async () => {
    if (!rec_template_data || rec_template_data.length === 0) return;

    const templates = rec_template_data.map((item: RecommendationTemplateRead) => ({
      id: item.id,
      name: item.name || 'Unnamed Template',
      template: item.template || null,
    }));

    if (templates.length === 0) {
      addToast(
        'No matching project plans were found for foundational projects. Please contact your administrator or nuLogic Support.',
        {
          appearance: 'warning',
          autoDismiss: true,
        }
      );
      return;
    }

    setIsSaving(true);
    try {
      const responses = await Promise.all(
        templates.map(template =>
          createAssessmentPlan({
            organizationAssessmentPeriodActionPlan: {
              organization: organizationId,
              assessment_period_id: 12,
              leader: memberId,
              recommendation: template.id,
              recommendation_name: template.name,
              recommendation_json: template.template,
              type_of: RecommendationType.FOUNDATIONS,
            },
          }).unwrap()
        )
      );

      const action_plan_id = responses?.[0]?.id;
      if (action_plan_id) {
        navigate(`/app/foundations/`);
      }
    } catch (err) {
      console.error('Project plan creation failed', err);
    } finally {
      setIsSaving(false);
      dispatch(actionPlanApi.util.resetApiState());
    }
  };

  if (!foundation_data) {
    return <LoadingSpinner />;
  }

  if (foundation_data.length === 0) {
    return (
      <PageContent>
        <PageSection>
          <LandingPage
            title="Welcome to Foundations!"
            subtitle="Before working on foundation projects, you'll need to generate a project plan..."
            buttonLabel="Generate Project Plan"
            navDestination="/app/foundations"
            onClick={onSave}
          />
        </PageSection>
      </PageContent>
    );
  }

  return (
    <PageContent data-testid="admin-foundations-projects-page">
      <PageHeader title={'Foundations'} data-testid="admin-foundations-projects-header" />
      <PageSection padbottom data-testid="admin-foundations-projects-content">
        <Table data-testid="admin-foundations-projects-table">
          <TableHeader>
            <HeaderCell width={4}>Project</HeaderCell>
            <HeaderCell width={8}>Description</HeaderCell>
            <HeaderCell width={4}>Progress</HeaderCell>
          </TableHeader>
          <TableBody>
            {foundation_data &&
              foundation_data.map((foundationProject, index) => (
                <TableRow
                  key={index}
                  onClick={() => handleRowClick(foundationProject.id)}
                  $selectable
                >
                  <TableCell $firstcol>{foundationProject.recommendation_name}</TableCell>
                  <TableCell>
                    <div title={foundationProject.recommendation_json.description}>
                      {foundationProject.recommendation_json.description.length > 240
                        ? `${foundationProject.recommendation_json.description.slice(0, 240)}...`
                        : foundationProject.recommendation_json.description}
                    </div>
                  </TableCell>
                  <TableCell style={{ gap: 10 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <DoughnutGauge
                        color={
                          (Number(foundationProject?.percent_complete) || 0) === 100
                            ? 'green'
                            : ''
                        }
                        complete={Number(foundationProject?.percent_complete) || 0}
                        percent={true}
                        size="tiny"
                      />
                      {`${foundationProject?.completed_task_count || 0} of ${foundationProject?.total_task_count || 0} tasks completed`}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </PageSection>
    </PageContent>
  );
};

export default FoundationProjects;
